'use client'
import React, {useContext, useEffect, useState} from 'react';
import {forwardRef} from '@chakra-ui/system';
import {Select, chakraComponents} from 'chakra-react-select';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Button,
  Text,
  Skeleton
} from '@chakra-ui/react';
import {Controller, useForm} from 'react-hook-form';
import useExperienceApi from '../../../experiences/lib/useExperienceApi';
import { AuthContext } from '@/auth/lib/authContext';
import useTranslation from '../../../lib/i18n/useTranslation';
const AddExperienceToPersonaForm = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const {
    hideSave,
    onSubmitted,
    onSubmitting,
    personaId,
    onCreateNew,
    groupId
  } = props;
  const {profile} = useContext(AuthContext);
  const {
    handleSubmit,
    errors,
    control,
    formState: { isSubmitting, isValid }
  } = useForm();
  const experienceApi = useExperienceApi();
  const [experienceOptionsList, setExperienceOptionsList] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const onSubmit = async (data) => {
    try {
      if(onSubmitting) {
        onSubmitting();
      }
      const experienceId = data.experience.value;
      if(groupId) {
        await experienceApi.linkManyPersonaToExperience(experienceId, [personaId], groupId);
      } else {
        await experienceApi.linkManyPersonaToExperience(experienceId, [personaId]);
      }
      if(onSubmitted) {
        onSubmitted();
      }
    } catch (e) {
      console.error(e);
    }
  };
  const fetchData = async () => {
    try {
      const [relatedExperiences, allExperiences] = await Promise.all([
        experienceApi.fetchExperiencesInPersonaAndInProductPersonaForUser(personaId, profile.id),
        experienceApi.searchExperiences('')
      ]);
      const relatedIds = new Set((relatedExperiences || []).map(e => e.id));
      const relatedOptions = (relatedExperiences || []).map(e => ({
        label: e.objective,
        value: e.id
      }));
      const otherOptions = (allExperiences || [])
        .filter(e => !relatedIds.has(e.id))
        .map(e => ({
          label: e.objective,
          value: e.id
        }));
      const groupedOptions = [];
      if (onCreateNew) {
        groupedOptions.push({
          label: '',
          options: [{ label: '+ Create New Experience', value: -1 }]
        });
      }
      if (relatedOptions.length > 0) {
        groupedOptions.push({
          label: 'In linked Personas & Products',
          options: relatedOptions
        });
      }
      if (otherOptions.length > 0) {
        groupedOptions.push({
          label: (relatedOptions.length > 0) ? 'Other Experiences' : '',
          options: otherOptions
        });
      }
      setExperienceOptionsList(groupedOptions);
    } catch (e) {
      console.error(e)
    }
  };
  useEffect(() => {
    if(personaId) {
      fetchData();
    }
  }, [personaId]);
  return (
    <>
        <form onSubmit={handleSubmit(onSubmit)} >
          <Controller
            control={control}
            name="experience"
            rules={{ required: "Please enter at least one experience" }}
            render={({
                       field: { onChange, onBlur, value, name, ref },
                       fieldState: { invalid, error }
                     }) => (
              <FormControl isInvalid={errors && errors.experience} mb={3} >
                <FormLabel>{t('experience.selectExperience')}</FormLabel>
                {!experienceOptionsList && (
                  <Skeleton w={"100%"} height={"30px"}/>
                )}
                {experienceOptionsList && (
                    <Select
                    options={experienceOptionsList}
                    menuPortalTarget={document.body} 
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    onInputChange={(e) => {
                      setInputValue(e);
                      if (experienceOptionsList && onCreateNew && experienceOptionsList[0]?.options?.[0]?.value === -1) {
                        const newList = [...experienceOptionsList];
                        newList[0] = {
                          ...newList[0],
                          options: [{
                            label: e ? `+ Create New Experience "${e}"` : '+ Create New Experience',
                            value: -1
                          }]
                        };
                        setExperienceOptionsList(newList);
                      }
                    }}
                    onChange={(e) => {
                      if(e.value === -1 && onCreateNew) {
                        onCreateNew(inputValue);
                      } else {
                        onChange(e);
                      }
                    }}
                    onBlur={onBlur}
                    value={value}
                  />
                )}
                <FormErrorMessage>{errors && errors.experience}</FormErrorMessage>
              </FormControl>
            )}
          />
          <Button
            style={{
              display: hideSave ? 'none' : 'inherit',
            }}
            ref={ref}
            isLoading={isSubmitting}
            disabled={!isValid}
            type='submit'
            my={2}
          >{t('common.save')}</Button>
        </form>
    </>
  );
});
export default AddExperienceToPersonaForm;
